// The boards a game table can show (the office runs the rules: src/games/), drawn on the panel's canvas (gomoku.js).
// Each board: its canvas size, draw(g, st, ui), and cellAt(px, py, ui) from a point on the canvas to a cell index in
// the game's own board string (row * width + column, as the office has it), or -1; size(ui) its [w, h] (coordinates
// take a margin). ui: {flip, hover, sel, targets (Set of cells), dots (Set of cells), side (my side, 0 watching),
// coords (show coordinates), nums (Map cell → move number, to number the stones), lastNo (the last move's number)}. Stone games are clicked on points; 象棋 and chess
// pick a piece and then where it goes (gomoku.js keeps the selection).
import { t, uiFont } from '/i18n.js';
import { XQ_CHARS, RIVER } from '/boards-xiangqi-data.js';
// icon: a pixel icon's name (public/icons.js). name and sides are read in the current language (public/i18n.js):
// name games.board.name.<kind>, sides by seat letter (SIDE).
const SIDE = { b: 'games.board.side.black', k: 'games.board.side.black', w: 'games.board.side.white', r: 'games.board.side.red' };
const game = (kind, icon, seat) => ({ kind, icon, seat, get name() { return t(`games.board.name.${kind}`); }, get sides() { return seat.map((c) => t(SIDE[c])); } });
export const GAMES = [
  game('gomoku', 'stones', ['b', 'w']),
  game('othello', 'othello', ['b', 'w']),
  game('go', 'go', ['b', 'w']),
  game('xiangqi', 'xiangqi', ['r', 'k']),
  game('chess', 'chess_n', ['w', 'b']),
];
/** [value, label]: the label read in the current language (a catalog key). */
const opt = (v, key, vars) => Object.defineProperty([v], 1, { get: () => t(key, vars), enumerable: true });
/** Each game's options in the picker: [{key, values: [[value, label]…]}] (the office cleans them: src/games/). */
export const OPTIONS = {
  gomoku: [{ key: 'renju', values: [opt(false, 'games.board.renju.off'), opt(true, 'games.board.renju.on')] }],
  go: [{ key: 'size', values: [opt(9, 'games.board.goSize', { n: 9 }), opt(13, 'games.board.goSize', { n: 13 }), opt(19, 'games.board.goSize', { n: 19 })] }],
};
export const gameInfo = (kind) => GAMES.find((g) => g.kind === kind) ?? GAMES[0];
/** A game's options as a short label in the current language ("" for the defaults that go without saying: no 禁手),
 *  as the office's label() has them (src/games/), from the table's opts: so the page never shows the office's Chinese. */
export const optsLabel = (kind, opts = {}) => (OPTIONS[kind] ?? []).map((o) => {
  const v = opts?.[o.key] ?? o.values[0][0];
  return v === false ? '' : o.values.find(([x]) => x === v)?.[1] ?? '';
}).filter(Boolean).join(' ');
/** A table's game on the map, in the current language (not the office's kindName, which is Chinese): the name and its
 *  options. No opts (an office from before gm_tables carried them): the name alone, never a size the table doesn't have. */
export const tableLabel = (kind, opts) => { const ol = kind && opts ? optsLabel(kind, opts) : ''; return gameInfo(kind ?? 'gomoku').name + (ol ? ` ${ol}` : ''); };
/** How a game ended, for the status line (resign / timeout / left are the table's; the rest the board's). */
// (read in the current language: games.board.reason.<reason>)
export const REASON = Object.defineProperties({}, Object.fromEntries(['five', 'full', 'count', 'score', 'mate', 'material', 'fifty', 'moves', 'resign', 'timeout', 'left']
  .map((r) => [r, { get: () => t(`games.board.reason.${r}`), enumerable: true }])));
export const stalemateText = (kind) => t(kind === 'chess' ? 'games.board.reason.stalemate' : 'games.board.reason.stalemateXq');

const INK = '#1b1410', CREAM = '#fffdf6', WOOD = '#e8bd6f', GRID = '#6b4a1f', LAST = '#ef4444', WIN = '#FDD03B';
const PX = 300, M = 13; // the board's size, and the margin the coordinates take
const label = (g, text, x, y, size = 9) => { g.fillStyle = 'rgba(27,20,16,.7)'; g.font = `600 ${size}px ${uiFont()}`; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(text, x, y); };
/** A move number on a stone (the last one red). */
function number(g, x, y, r, n, onBlack, last) {
  g.fillStyle = last ? LAST : onBlack ? CREAM : INK;
  g.font = `bold ${Math.round(r * (n > 99 ? 0.85 : 1.05))}px ${uiFont()}`; g.textAlign = 'center'; g.textBaseline = 'middle';
  g.fillText(String(n), x, y + 0.5);
}

function stone(g, x, y, r, c) {
  g.beginPath(); g.arc(x, y, r, 0, 7);
  g.fillStyle = c === '1' ? INK : CREAM; g.fill();
  g.lineWidth = 1.5; g.strokeStyle = INK; g.stroke();
}
function dot(g, x, y, r, color) { g.fillStyle = color; g.beginPath(); g.arc(x, y, r, 0, 7); g.fill(); }

/** Star points (hoshi) for an N-line board. */
const starsFor = (N) => N === 15 ? [[3, 3], [11, 3], [7, 7], [3, 11], [11, 11]] : N === 9 ? [[2, 2], [6, 2], [4, 4], [2, 6], [6, 6]]
  : N === 13 ? [[3, 3], [9, 3], [6, 6], [3, 9], [9, 9]] : [3, 9, 15].flatMap((x) => [3, 9, 15].map((y) => [x, y]));
/** A board of N×N points (五子棋 15; 围棋 9, 13 or 19 from the state, ui.n): stones on the intersections, drawn at
 *  300 px, or 440 px for 19 路 so the stones stay readable. Coordinates: x across the top, y down the left, 0-based
 *  (as agents write moves, and as deck look gomoku draws them). ui.forbid: points to mark ✕ (禁手 for black). */
function pointBoard(N0) {
  const px = (N) => (N >= 19 ? 440 : PX);
  const geo = (ui) => { const N = ui?.n ?? N0, P = px(N), o = ui?.coords ? M : 0, cell = (P - o) / N, pad = cell / 2; return { N, P, o, cell, pad, at: (i) => o + pad + i * cell }; };
  return {
    size: (ui) => { const P = px(ui?.n ?? N0); return [P, P]; },
    cellAt(x0, y0, ui) { const { N, o, cell, pad } = geo(ui), x = Math.round((x0 - o - pad) / cell), y = Math.round((y0 - o - pad) / cell); return x >= 0 && y >= 0 && x < N && y < N ? y * N + x : -1; },
    draw(g, st, ui) {
      const { N, P, o, cell, at } = geo(ui), fs = N > 15 ? 9 : N > 10 ? 8 : 9;
      g.fillStyle = WOOD; g.fillRect(0, 0, P, P);
      if (ui.coords) for (let i = 0; i < N; i++) { label(g, String(i), at(i), o / 2 + 1, fs); label(g, String(i), o / 2, at(i), fs); }
      g.strokeStyle = GRID; g.lineWidth = 1;
      for (let i = 0; i < N; i++) {
        g.beginPath(); g.moveTo(at(0), Math.round(at(i)) + 0.5); g.lineTo(at(N - 1), Math.round(at(i)) + 0.5); g.stroke();
        g.beginPath(); g.moveTo(Math.round(at(i)) + 0.5, at(0)); g.lineTo(Math.round(at(i)) + 0.5, at(N - 1)); g.stroke();
      }
      g.lineWidth = 2; g.strokeRect(at(0), at(0), at(N - 1) - at(0), at(N - 1) - at(0));
      for (const [x, y] of starsFor(N)) dot(g, at(x), at(y), N > 10 ? 2.5 : 3, GRID);
      const v = st?.view; if (!v) return;
      const r = cell * (N > 10 ? 0.44 : 0.45), b = v.board;
      for (const i of ui.forbid ?? []) { // 禁手: a small red ✕ where black may not play
        const x = at(i % N), y = at(Math.floor(i / N)), d = cell * 0.22;
        g.strokeStyle = LAST; g.lineWidth = 2; g.beginPath(); g.moveTo(x - d, y - d); g.lineTo(x + d, y + d); g.moveTo(x + d, y - d); g.lineTo(x - d, y + d); g.stroke();
      }
      for (let i = 0; i < N * N; i++) if (b[i] !== '0') {
        const x = at(i % N), y = at(Math.floor(i / N));
        stone(g, x, y, r, b[i]);
        const n = ui.nums?.get(i); if (n) number(g, x, y, r, n, b[i] === '1', n === ui.lastNo);
      }
      const line = st.phase === 'over' ? v.line ?? [] : [];
      if (line.length) { // the winning five (or more): a yellow line through them, end to end
        const a = line[0], z = line[line.length - 1], seg = (w, c) => {
          g.strokeStyle = c; g.lineWidth = w; g.lineCap = 'round';
          g.beginPath(); g.moveTo(at(a % N), at(Math.floor(a / N))); g.lineTo(at(z % N), at(Math.floor(z / N))); g.stroke();
        };
        seg(8, INK); seg(5, WIN); g.lineCap = 'butt';
      }
      if (v.last >= 0 && !ui.nums?.size) dot(g, at(v.last % N), at(Math.floor(v.last / N)), r * 0.32, LAST);
      if (ui.hover >= 0 && b[ui.hover] === '0' && !(ui.forbid ?? []).includes(ui.hover)) {
        g.fillStyle = ui.side === 1 ? 'rgba(27,20,16,.45)' : 'rgba(255,253,246,.75)';
        g.beginPath(); g.arc(at(ui.hover % N), at(Math.floor(ui.hover / N)), r, 0, 7); g.fill();
      }
    },
  };
}

/** 黑白棋: 8×8 squares on green; my legal moves as small dots. Coordinates x across, y down, 0-7. */
function othelloBoard() {
  const N = 8, geo = (ui) => { const o = ui?.coords ? M : 0, cell = (PX - o) / N; return { o, cell, mid: (i) => o + (i + 0.5) * cell }; };
  return {
    size: () => [PX, PX],
    cellAt(px, py, ui) { const { o, cell } = geo(ui), x = Math.floor((px - o) / cell), y = Math.floor((py - o) / cell); return x >= 0 && y >= 0 && x < N && y < N ? y * N + x : -1; },
    draw(g, st, ui) {
      const { o, cell, mid } = geo(ui);
      g.fillStyle = WOOD; g.fillRect(0, 0, PX, PX);
      if (ui.coords) for (let i = 0; i < N; i++) { label(g, String(i), mid(i), o / 2 + 1); label(g, String(i), o / 2, mid(i)); }
      g.fillStyle = '#3f9a5a'; g.fillRect(o, o, PX - o, PX - o);
      g.strokeStyle = '#1f5130'; g.lineWidth = 1.5;
      for (let i = 1; i < N; i++) {
        g.beginPath(); g.moveTo(o + i * cell, o); g.lineTo(o + i * cell, PX); g.stroke();
        g.beginPath(); g.moveTo(o, o + i * cell); g.lineTo(PX, o + i * cell); g.stroke();
      }
      for (const [x, y] of [[2, 2], [6, 2], [2, 6], [6, 6]]) dot(g, o + x * cell, o + y * cell, 3, '#1f5130');
      const v = st?.view; if (!v) return;
      for (let i = 0; i < N * N; i++) if (v.board[i] !== '0') {
        const x = mid(i % N), y = mid(Math.floor(i / N));
        stone(g, x, y, cell * 0.4, v.board[i]);
        const n = ui.nums?.get(i); if (n) number(g, x, y, cell * 0.4, n, v.board[i] === '1', n === ui.lastNo);
      }
      if (v.last >= 0 && !ui.nums?.size) dot(g, mid(v.last % N), mid(Math.floor(v.last / N)), cell * 0.1, LAST);
      for (const i of ui.dots) dot(g, mid(i % N), mid(Math.floor(i / N)), cell * (i === ui.hover ? 0.4 : 0.12), ui.side === 1 ? 'rgba(27,20,16,.45)' : 'rgba(255,253,246,.8)');
    },
  };
}

/** 中国象棋: 9×10 points, the river and the palaces; round pieces with their characters. Red at the bottom unless
 *  I play black (flip). */
function xiangqiBoard() {
  const W = 9, H = 10, cell = PX / W, pad = cell / 2, h = Math.round(pad * 2 + cell * (H - 1));
  // coordinates: files a-i under the board, ranks 0-9 on the left (ICCS, as agents write moves); they shrink the board
  const k = (ui) => (ui?.coords ? (PX - M) / PX : 1), o = (ui) => (ui?.coords ? M : 0);
  const col = (f, flip, ui) => o(ui) + (pad + (flip ? W - 1 - f : f) * cell) * k(ui), row = (r, flip, ui) => (pad + (flip ? r : H - 1 - r) * cell) * k(ui);
  return {
    size: (ui) => [PX, ui?.coords ? Math.round(h * k(ui) + M) : h],
    cellAt(px, py, ui) {
      const c = Math.round(((px - o(ui)) / k(ui) - pad) / cell), rr = Math.round((py / k(ui) - pad) / cell);
      if (c < 0 || rr < 0 || c >= W || rr >= H) return -1;
      const f = ui.flip ? W - 1 - c : c, r = ui.flip ? rr : H - 1 - rr;
      return r * W + f;
    },
    draw(g, st, ui) {
      const fl = ui.flip, X = (f) => col(f, fl, ui), Y = (r) => row(r, fl, ui), sc = k(ui), [, hh] = this.size(ui);
      g.fillStyle = WOOD; g.fillRect(0, 0, PX, hh);
      if (ui.coords) { for (let f = 0; f < W; f++) label(g, 'abcdefghi'[f], X(f), hh - M / 2); for (let r = 0; r < H; r++) label(g, String(r), M / 2, Y(r)); }
      const cs = cell * sc; // a point's spacing as drawn
      g.strokeStyle = GRID; g.lineWidth = 1;
      const ln = (f0, r0, f1, r1) => { g.beginPath(); g.moveTo(X(f0), Y(r0)); g.lineTo(X(f1), Y(r1)); g.stroke(); };
      for (let r = 0; r < H; r++) ln(0, r, W - 1, r);
      for (let f = 0; f < W; f++) { if (f === 0 || f === W - 1) ln(f, 0, f, H - 1); else { ln(f, 0, f, 4); ln(f, 5, f, 9); } }
      ln(3, 0, 5, 2); ln(5, 0, 3, 2); ln(3, 9, 5, 7); ln(5, 9, 3, 7); // the palaces
      g.lineWidth = 2; g.strokeRect(Math.min(X(0), X(8)), Y(fl ? 0 : 9), Math.abs(X(8) - X(0)), Math.abs(Y(9) - Y(0)));
      g.fillStyle = GRID; g.font = `bold ${Math.round(cs * 0.5)}px "Songti SC", "STSong", serif`; g.textAlign = 'center'; g.textBaseline = 'middle';
      const ry = (Y(4) + Y(5)) / 2, lx = Math.min(X(2), X(6)), rx = Math.max(X(2), X(6));
      g.fillText(RIVER[fl ? 1 : 0], lx, ry); g.fillText(RIVER[fl ? 0 : 1], rx, ry);
      const v = st?.view; if (!v) return;
      if (v.last) for (const i of v.last) { g.strokeStyle = LAST; g.lineWidth = 2; g.strokeRect(X(i % W) - cs * 0.47, Y(Math.floor(i / W)) - cs * 0.47, cs * 0.94, cs * 0.94); }
      const r = cs * 0.43;
      g.font = `bold ${Math.round(cs * 0.5)}px "Kaiti SC", "STKaiti", "KaiTi", "Songti SC", serif`;
      for (let i = 0; i < W * H; i++) {
        const c = v.board[i]; if (c === '.') continue;
        const x = X(i % W), y = Y(Math.floor(i / W)), red = c === c.toUpperCase(), ink = red ? '#c62828' : INK;
        g.beginPath(); g.arc(x + 1, y + 2, r, 0, 7); g.fillStyle = 'rgba(27,20,16,.3)'; g.fill();
        g.beginPath(); g.arc(x, y, r, 0, 7); g.fillStyle = i === ui.sel ? '#FDD03B' : '#fbe7c2'; g.fill();
        g.lineWidth = 2; g.strokeStyle = INK; g.stroke();
        g.beginPath(); g.arc(x, y, r * 0.8, 0, 7); g.lineWidth = 1; g.strokeStyle = ink; g.stroke();
        if (v.check && (c === 'K' || c === 'k') && (c === 'K') === (st.turn === 1)) { g.beginPath(); g.arc(x, y, r + 2, 0, 7); g.lineWidth = 3; g.strokeStyle = LAST; g.stroke(); }
        g.fillStyle = ink; g.fillText(XQ_CHARS[c], x, y + 1);
      }
      for (const i of ui.targets) { const x = X(i % W), y = Y(Math.floor(i / W)); dot(g, x, y, v.board[i] === '.' ? cs * 0.14 : r * 0.35, 'rgba(22,163,74,.85)'); }
    },
  };
}

const CHESS_GLYPH = { K: '♚', Q: '♛', R: '♜', B: '♝', N: '♞', P: '♟' };
/** 国际象棋: 8×8 squares, white at the bottom unless I play black (flip). */
function chessBoard() {
  const N = 8, cell = PX / N;
  const sx = (f, fl) => (fl ? N - 1 - f : f) * cell, sy = (r, fl) => (fl ? r : N - 1 - r) * cell;
  return {
    size: () => [PX, PX],
    cellAt(px, py, ui) {
      const c = Math.floor(px / cell), rr = Math.floor(py / cell);
      if (c < 0 || rr < 0 || c >= N || rr >= N) return -1;
      return (ui.flip ? rr : N - 1 - rr) * N + (ui.flip ? N - 1 - c : c);
    },
    draw(g, st, ui) {
      const fl = ui.flip, v = st?.view;
      for (let r = 0; r < N; r++) for (let f = 0; f < N; f++) {
        const i = r * N + f, lastSq = v?.last?.includes(i);
        g.fillStyle = (r + f) % 2 ? '#f3dcb1' : '#b98a55';
        g.fillRect(sx(f, fl), sy(r, fl), cell, cell);
        if (lastSq || i === ui.sel) { g.fillStyle = i === ui.sel ? 'rgba(253,208,59,.75)' : 'rgba(239,68,68,.3)'; g.fillRect(sx(f, fl), sy(r, fl), cell, cell); }
      }
      if (ui.coords) { // files a-h along the bottom, ranks 1-8 down the left, inside the edge squares (UCI, as agents write moves)
        g.font = `bold 9px ${uiFont()}`; g.textAlign = 'left'; g.textBaseline = 'alphabetic';
        for (let f = 0; f < N; f++) { g.fillStyle = 'rgba(27,20,16,.6)'; g.fillText('abcdefgh'[f], sx(f, fl) + 2, PX - 2); }
        for (let r = 0; r < N; r++) { g.fillStyle = 'rgba(27,20,16,.6)'; g.fillText(String(r + 1), 2, sy(r, fl) + 10); }
      }
      if (!v) return;
      g.font = `${Math.round(cell * 0.82)}px "Apple Symbols", "Segoe UI Symbol", "DejaVu Sans", serif`; g.textAlign = 'center'; g.textBaseline = 'middle';
      for (let i = 0; i < N * N; i++) {
        const c = v.board[i]; if (c === '.') continue;
        const x = sx(i % N, fl) + cell / 2, y = sy(Math.floor(i / N), fl) + cell / 2 + cell * 0.04, white = c === c.toUpperCase();
        if (v.check && (c === 'K' || c === 'k') && white === (st.turn === 1)) { g.beginPath(); g.arc(x, y - cell * 0.04, cell * 0.45, 0, 7); g.fillStyle = 'rgba(239,68,68,.55)'; g.fill(); }
        const glyph = CHESS_GLYPH[c.toUpperCase()] + '︎';
        g.lineWidth = 3; g.strokeStyle = white ? INK : CREAM; g.lineJoin = 'round'; g.strokeText(glyph, x, y);
        g.fillStyle = white ? CREAM : INK; g.fillText(glyph, x, y);
      }
      for (const i of ui.targets) { const x = sx(i % N, fl) + cell / 2, y = sy(Math.floor(i / N), fl) + cell / 2; dot(g, x, y, v.board[i] === '.' ? cell * 0.14 : cell * 0.2, 'rgba(22,163,74,.85)'); }
    },
  };
}

const BOARDS = {
  gomoku: pointBoard(15), go: pointBoard(9),
  othello: othelloBoard(), xiangqi: xiangqiBoard(), chess: chessBoard(),
};
export const boardOf = (kind) => BOARDS[kind] ?? BOARDS.gomoku;

/** A move string → [from, to] cell indexes (象棋 ICCS "h2e2", chess UCI "e2e4"/"e7e8q"), or a stone game's "x y" → cell. */
export function moveCells(kind, mv) {
  if (kind === 'xiangqi') { const c = (s, k) => (+s[k + 1]) * 9 + s.charCodeAt(k) - 97; return [c(mv, 0), c(mv, 2)]; }
  if (kind === 'chess') { const c = (s, k) => (+s[k + 1] - 1) * 8 + s.charCodeAt(k) - 97; return [c(mv, 0), c(mv, 2)]; }
  return null;
}
/** A cell → the move string's half for it (象棋 / chess squares). */
export function cellName(kind, i) {
  if (kind === 'xiangqi') return 'abcdefghi'[i % 9] + Math.floor(i / 9);
  return 'abcdefgh'[i % 8] + (Math.floor(i / 8) + 1);
}
/** Is the piece on board char `c` mine (side 1: upper case)? */
export const pieceSide = (c) => (c === '.' || c === '0' ? 0 : c === c.toUpperCase() ? 1 : 2);
